import { Map, OrderedMap } from "immutable";
import { deserializeFavorite, deserializeSingleFavorite, Favorite } from "lib/api/records";
import { apiv3 } from "lib/apiv3";
import fetchJSON from "lib/fetchJSON";
import { loadAllPages } from "lib/loadAllPages";
import { fulfilled, pending } from "lib/promiseMiddlewareHelpers";

export const LOAD_USER_FAVORITES = "favorites/LOAD_USER_FAVORITES";
export const PIN = "favorites/PIN";
export const UNPIN = "favorites/UNPIN";

// quick note - the load logic here is oversimplified and shouldn't be
// used as a 'good example' for how to do loading flags in a reducer
const initialState = Map({
  favorites: OrderedMap({}),
  favoritesByType: Map({}), // type -> id -> favorite obj
  loadPending: false,
});

export const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case pending(LOAD_USER_FAVORITES): {
      return state.merge({ loadPending: true });
    }
    case fulfilled(LOAD_USER_FAVORITES): {
      const newFavorites = action.payload.results.map(deserializeFavorite);

      // append new entries
      const favorites = state.get("favorites").merge(newFavorites.map((f) => [f.id, f]));

      const favoritesByType = state.get("favoritesByType").withMutations((fBT) => {
        newFavorites.forEach((f) => {
          fBT.setIn([f.get("itemType"), f.get("itemId")], f);
        });
      });

      return state.merge({
        loadPending: false,
        favorites: favorites,
        favoritesByType: favoritesByType,
      });
    }
    case pending(PIN):
      return state.setIn(
        ["favoritesByType", action.meta.type, action.meta.id],
        Favorite({ itemType: action.meta.type, itemId: action.meta.id })
      );
    case fulfilled(PIN):
      const newFavorite = deserializeSingleFavorite(action.payload.favorite);
      return state
        .setIn(["favoritesByType", newFavorite.itemType, newFavorite.itemId], newFavorite)
        .setIn(["favorites", newFavorite.id], newFavorite);
    case pending(UNPIN):
      const favorite = state.getIn(["favoritesByType", action.meta.type, action.meta.id]);
      return state
        .set("favorites", state.get("favorites").delete(favorite.id))
        .set("favoritesByType", state.get("favoritesByType").deleteIn([favorite.itemType, favorite.itemId]));
    default:
      return state;
  }
};

export const loadUserFavorites = (userId, params) => ({
  type: LOAD_USER_FAVORITES,
  payload: apiv3.users.pinned(userId, {
    ...params,
    order_by: "created_at",
    order_dir: "desc",
  }),
  meta: { userId },
});

export const loadAllUserFavorites = (userId, params) => (dispatch) => {
  const loadFavoritesPage = (pageInfo) => {
    const { pageIndex, perPage } = pageInfo;
    return dispatch(
      loadUserFavorites(userId, {
        page: pageIndex,
        per_page: perPage,
        order_by: "created_at",
        extras: true,
        ...params,
      })
    );
  };

  const isLastPage = ({ perPage, pageIndex, pageResults }) =>
    perPage * (pageIndex + 1) >= pageResults.value.total_count;

  return loadAllPages({
    promiseProducer: loadFavoritesPage,
    perPage: 50,
    isLastPage,
  });
};

export const pin = (type, id) => ({
  type: PIN,
  payload: fetchJSON(`/remember/${type}/${id}.json`, {}, { method: "POST" }),
  meta: { id, type },
});

export const unpin = (type, id) => ({
  type: UNPIN,
  payload: fetchJSON(`/forget/${type}/${id}.json`, {}, { method: "POST" }),
  meta: { id, type },
});
